/**
 * Audio Playlist Service - Music Player Architecture
 * Treats course modules like songs in a playlist
 * - Category = Artist
 * - Course = Album
 * - Module = Song
 */

import { audioMetadataCache } from './AudioMetadataCache';
import { GeneratedContentService } from '@/services/generatedContentService';
import { GlobalAudioService } from './GlobalAudioService';

export interface PlaylistTrack {
  moduleId: string;
  courseId: string;
  moduleTitle: string;
  audioUrl: string | null;
  duration: number;
  index: number;
}

class AudioPlaylistServiceClass {
  private playlist: PlaylistTrack[] = [];
  private currentCourseId: string | null = null;
  private currentTrackIndex: number = -1;
  private listeners: Set<(playlist: PlaylistTrack[], currentIndex: number) => void> = new Set();

  /**
   * Load entire course playlist at once (like loading an album)
   */
  async loadCoursePlaylist(courseId: string, modules: Array<{ id: string; title: string }>): Promise<void> {
    try {
      console.log('🎵 [AudioPlaylist] Loading course playlist:', courseId, `(${modules.length} modules)`);
      
      this.currentCourseId = courseId;
      this.playlist = [];
      
      if (!modules || modules.length === 0) {
        console.warn('[AudioPlaylist] No modules provided');
        this.notifyListeners();
        return;
      }

      // Fetch audio metadata for all modules in one batch
      const audioPromises = modules.map(async (module, index) => {
        try {
          // Check cache first
          let audio = audioMetadataCache.get(module.id, courseId);
          
          // Fetch from DB if not cached
          if (!audio) {
            audio = await GeneratedContentService.getModuleAudio(module.id, courseId);
          }

          return {
            moduleId: module.id,
            courseId,
            moduleTitle: module.title,
            audioUrl: audio?.audioUrl || null,
            duration: audio?.duration || 0,
            index
          };
        } catch (error) {
          console.error(`[AudioPlaylist] Failed to load audio for module ${module.id}:`, error);
          return {
            moduleId: module.id,
            courseId,
            moduleTitle: module.title,
            audioUrl: null,
            duration: 0,
            index
          };
        }
      });

      this.playlist = await Promise.all(audioPromises);
      console.log('✅ [AudioPlaylist] Playlist loaded:', this.playlist.length, 'tracks');
      this.notifyListeners();
    } catch (error) {
      console.warn('[AudioPlaylist] Failed to load (non-critical):', error);
      // Reset to safe state instead of throwing
      this.playlist = [];
      this.currentTrackIndex = -1;
      this.currentCourseId = null;
      this.notifyListeners();
      // DON'T throw - page must render
    }
  }

  /**
   * Set current module without auto-playing
   */
  setCurrentModule(moduleId: string): void {
    const index = this.playlist.findIndex(track => track.moduleId === moduleId);
    if (index !== -1) {
      this.currentTrackIndex = index;
      console.log('🎵 [AudioPlaylist] Current module set:', moduleId, `(track ${index + 1}/${this.playlist.length})`);
      this.notifyListeners();
    }
  }

  /**
   * Get current playing track info
   */
  getCurrentTrack(): PlaylistTrack | null {
    if (this.currentTrackIndex === -1 || this.playlist.length === 0) {
      return null;
    }
    return this.playlist[this.currentTrackIndex] || null;
  }

  /**
   * Get playlist info
   */
  getPlaylistInfo(): { currentIndex: number; total: number; courseId: string | null } {
    return {
      currentIndex: this.currentTrackIndex,
      total: this.playlist.length,
      courseId: this.currentCourseId
    };
  }

  /**
   * Check if module has audio available
   */
  hasAudio(moduleId: string): boolean {
    const track = this.playlist.find(t => t.moduleId === moduleId);
    return !!track?.audioUrl;
  }

  /**
   * Navigate to next module (NO auto-play, just navigation)
   */
  getNextModule(): PlaylistTrack | null {
    if (this.currentTrackIndex < this.playlist.length - 1) {
      return this.playlist[this.currentTrackIndex + 1];
    }
    return null;
  }

  /**
   * Navigate to previous module (NO auto-play, just navigation)
   */
  getPreviousModule(): PlaylistTrack | null {
    if (this.currentTrackIndex > 0) {
      return this.playlist[this.currentTrackIndex - 1];
    }
    return null;
  }

  /**
   * Clear playlist
   */
  clearPlaylist(): void {
    this.playlist = [];
    this.currentTrackIndex = -1;
    this.currentCourseId = null;
    this.notifyListeners();
  }

  /**
   * Subscribe to playlist changes
   */
  subscribe(listener: (playlist: PlaylistTrack[], currentIndex: number) => void): () => void {
    this.listeners.add(listener);
    listener(this.playlist, this.currentTrackIndex);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.playlist, this.currentTrackIndex));
  }
}

export const audioPlaylistService = new AudioPlaylistServiceClass();
